/**
 * Which platform origins this app will talk to.
 *
 * This is the security decision the bridge is built around, so it is the first
 * file in the app rather than a detail buried in the bootstrap. An embedded app
 * that accepts a bridge handshake from whoever framed it hands its context and
 * its API token to any site that puts it in an iframe.
 *
 * Resolution order:
 *
 * 1. `?platformOrigin=…` — pins to exactly one origin. A review affordance, for
 *    pointing this deployment at a platform running somewhere new without a
 *    rebuild. Still a pin, so still safe.
 * 2. The origins compiled in at build time (`PLATFORM_ORIGINS`). **This is what
 *    a real app does**: a hard-coded list, or one from your own build config.
 * 3. `ancestorOrigins` — trusting whoever framed us. Unsafe, off unless the
 *    build sets `DEMO_TRUST_EMBEDDER`, and the UI shows a warning banner when
 *    it is what ends up being used. It exists so one deployment of this demo
 *    can be reviewed against ephemeral preview environments whose origins are
 *    not known at build time. Never do this in a real app.
 */

/** Replaced at build time — see scripts/build.ts. */
declare const PLATFORM_ORIGINS: string[]
declare const DEMO_TRUST_EMBEDDER: boolean

export interface OriginResolution {
	origins: string[]
	source: 'query-pin' | 'compiled-in' | 'trusted-embedder'
	/** True when the app is trusting whoever framed it. Render a warning. */
	unsafe: boolean
}

export interface OriginResolutionInput {
	/** `location.search`, e.g. `'?platformOrigin=https://app.example'`. */
	search: string
	/** `location.ancestorOrigins`, if the browser provides it — Firefox does not. */
	ancestorOrigins?: readonly string[] | undefined
	compiledIn: readonly string[]
	trustEmbedder: boolean
}

/**
 * Pure so it can be tested without a browser. `main.ts` supplies the real
 * `location` values.
 */
export function resolvePlatformOrigins(input: OriginResolutionInput): OriginResolution {
	const pinned = new URLSearchParams(input.search).get('platformOrigin')
	if (pinned) {
		return { origins: [pinned], source: 'query-pin', unsafe: false }
	}

	const compiled = input.compiledIn.filter((origin) => origin !== '')
	if (compiled.length > 0) {
		return { origins: [...compiled], source: 'compiled-in', unsafe: false }
	}

	if (input.trustEmbedder && input.ancestorOrigins && input.ancestorOrigins.length > 0) {
		return { origins: [...input.ancestorOrigins], source: 'trusted-embedder', unsafe: true }
	}

	// Nothing to talk to. connect() will reject, and the "not embedded" view
	// explains why — which is the right outcome for a misconfigured build.
	return { origins: [], source: 'compiled-in', unsafe: false }
}

/** The resolution for the running page. */
export function currentOriginResolution(): OriginResolution {
	return resolvePlatformOrigins({
		search: location.search,
		ancestorOrigins: location.ancestorOrigins ? Array.from(location.ancestorOrigins) : undefined,
		compiledIn: typeof PLATFORM_ORIGINS === 'undefined' ? [] : PLATFORM_ORIGINS,
		trustEmbedder: typeof DEMO_TRUST_EMBEDDER === 'undefined' ? false : DEMO_TRUST_EMBEDDER,
	})
}
